// Kanemori Sanctuary countryside (M10): where the trees, road lanterns and torii stand. Pure and deterministic (no
// DOM), shared by the 2x art (hd-sanctuary-country.ts places the sprites) and the client-only wildlife and petals
// (country-fx.ts drifts petals off the sakura crowns). The same list is the collision: every tree has a small round trunk
// (	runkHit, called from sim.ts for walkers), so client prediction and the server see identical trunks. Trees keep at
// least TRUNK_GAP px between trunks, so a walker always finds a way between them and no ring of trunks can wall anyone in.
import { WORLD } from './data';
import { T, TILE, tileAt, type GameMap } from './map';
import { sampleRoad, countryField, FEATURES, featureAt, type RoadSample, type Feature } from './country';
import { hash2 } from './util';

export type TreeKind = 'cedar' | 'pine' | 'maple' | 'sakura' | 'bamboo' | 'broad';
/** a tree's foot in world px; v (0..1) picks its variant */
export interface Tree { x: number; y: number; kind: TreeKind; v: number; /** trunk radius in world px */ r: number }
const TRUNK_R: Record<TreeKind, number> = { cedar: 3.2, pine: 2.8, maple: 3, sakura: 3.2, bamboo: 2, broad: 3.2 };
/** least distance between two trunk centres (px): the walker is 10 px wide, trunks up to 6.4, so the gap is always >= 16 px */
export const TRUNK_GAP = 24;

/** first tile row of the countryside forest (the ridge line above it stays solid monastery forest) */
export const COUNTRY_Y0 = 46;
const strip = (tx: number, ty: number) => tx >= 258 && tx < 334 && ty >= COUNTRY_Y0 && ty < 142;

const pathCache = new Map<Feature, RoadSample[]>();
/** a feature's path as a sampled spline (stair, torii row, stream, grove path) */
export function featurePath(f: Feature): RoadSample[] {
  let s = pathCache.get(f);
  if (!s) pathCache.set(f, s = f.points && f.points.length > 1 ? sampleRoad({ name: f.name, half: 1, verge: 1, points: f.points }, 0.25) : []);
  return s;
}
/** distance (tiles) from a point to a feature's path */
export function pathDist(f: Feature, x: number, y: number) {
  let d = Infinity; for (const s of featurePath(f)) d = Math.min(d, Math.hypot(s.x - x, s.y - y)); return d;
}

const plans = new WeakMap<GameMap, { trees: Tree[]; lanterns: { x: number; y: number; side: number }[]; torii: RoadSample[] }>();
export function countryPlan(m: GameMap) {
  let p = plans.get(m); if (p) return p;
  const f = countryField(m.w, m.h), road = WORLD.sanctuary?.road;
  const trees: Tree[] = [], lanterns: { x: number; y: number; side: number }[] = [], torii: RoadSample[] = [];
  p = { trees, lanterns, torii }; plans.set(m, p);
  if (!f || !road) return p;
  const V = road.verge, field = (tx: number, ty: number) => f[ty * m.w + tx] ?? Infinity;
  const t = (tx: number, ty: number) => tileAt(m, tx, ty);
  const feats = FEATURES(), paths = feats.filter(q => q.points && q.kind !== 'bamboo');
  const nearPath = (x: number, y: number, r: number) => paths.some(q => x >= q.x - 2 && y >= q.y - 2 && x < q.x + q.w + 2 && y < q.y + q.h + 2 && pathDist(q, x, y) < r);
  // crowns rise over the ground north of the foot: keep them off the asphalt and the courtyards (sakura may lean over)
  const clearAbove = (tx: number, ty: number, rows: number) => {
    for (let dy = 1; dy <= rows; dy++) { const k = t(tx, ty - dy); if (k === T.PLAZA || k === T.LOW || field(tx, ty - dy) <= V) return false; }
    return true;
  };
  // the tea field and its pickers' path stay clear of trees: a margin of 1 tile round it, and 4 tiles to the south, where a
  // tall crown (drawn above its foot) would rise over the rows
  const tea = feats.find(q => q.kind === 'tea');
  const inTeaKeepOut = (x: number, y: number) => !!tea && x >= tea.x - 1 && x < tea.x + tea.w + 1 && y >= tea.y - 1 && y < tea.y + tea.h + 4;
  const planted: Tree[] = [];
  for (let cy = COUNTRY_Y0; cy < 142; cy += 2) for (let cx = 258; cx < 334; cx += 2) {
    const a = hash2(cx * 7 + 1, cy * 13 + 5), b = hash2(cy * 3 + 9, cx * 11 + 2), c = hash2(cx + 77, cy + 31);
    const fx = cx + 0.25 + a * 1.5, fy = cy + 0.4 + b * 1.4, tx = Math.floor(fx), ty = Math.floor(fy);
    if (!strip(tx, ty) || t(tx, ty) !== T.BOLLARD) continue;
    const d = field(tx, ty);
    if (d <= V + 0.4) continue;
    const ft = featureAt(tx, ty);
    let kind: TreeKind, dens = 0.62;
    if (ft?.kind === 'bamboo') { kind = 'bamboo'; dens = 0.7; }
    else if (ft && ['paddies', 'onsen', 'reststop', 'garden', 'stream', 'tea'].includes(ft.kind)) continue;
    else if (ft?.kind === 'meadow') { kind = c < 0.5 ? 'maple' : 'broad'; dens = 0.12; }
    else if (featureAt(tx, ty, 'sakura') && d < V + 3.2) { kind = 'sakura'; dens = 0.95; }
    else if (ty < 80) kind = c < 0.72 ? 'cedar' : c < 0.86 ? 'maple' : 'broad';
    else kind = c < 0.44 ? 'cedar' : c < 0.64 ? 'pine' : c < 0.84 ? 'broad' : 'maple';
    if (hash2(cx * 5 + 3, cy * 17 + 1) > dens) continue;
    if (nearPath(fx, fy, kind === 'bamboo' ? 1.1 : 1.6)) continue;
    if (inTeaKeepOut(fx, fy)) continue;
    if (!clearAbove(tx, ty, kind === 'sakura' ? 1 : kind === 'bamboo' ? 2 : 3)) continue;
    planted.push({ x: fx * TILE, y: fy * TILE, kind, v: c, r: TRUNK_R[kind] });
  }
  // the strolling garden: hand-set maples and pines round the pond, a weeping sakura by the tea house
  const gd = feats.find(q => q.kind === 'garden');
  if (gd) for (const [dx, dy, kind, v] of [[1.5, 1.2, 'maple', 0.2], [4.6, 1.6, 'pine', 0.4], [15.5, 2.2, 'sakura', 0.7], [1.2, 6.5, 'pine', 0.8],
    [2.2, 12.5, 'maple', 0.6], [10.2, 12.8, 'maple', 0.1], [16.6, 9.6, 'pine', 0.3], [17.4, 14.6, 'maple', 0.9], [13.4, 15.6, 'sakura', 0.3], [5.5, 15.4, 'pine', 0.55]] as const)
    trees.push({ x: (gd.x + dx) * TILE, y: (gd.y + dy) * TILE, kind, v, r: TRUNK_R[kind] });
  const on = feats.find(q => q.kind === 'onsen');
  if (on) for (const [dx, dy] of [[0.8, 3], [9.6, 4.5], [0.8, 12.5], [9, 13.5]]) trees.push({ x: (on.x + dx) * TILE, y: (on.y + dy) * TILE, kind: 'bamboo', v: dx / 11, r: TRUNK_R.bamboo });
  // a few sakura up by the temple: either side of the pilgrim stair's top
  for (const [tx, ty] of [[279.5, 64.5], [281.5, 60.2], [266.5, 61.2], [260.5, 70.5]]) trees.push({ x: tx * TILE, y: ty * TILE, kind: 'sakura', v: hash2(tx, ty), r: TRUNK_R.sakura });

  // the hand-set ones obey the same rules as the planted ones: on forest floor, off the road
  for (let i = trees.length - 1; i >= 0; i--) { const tx = Math.floor(trees[i].x / TILE), ty = Math.floor(trees[i].y / TILE); if (t(tx, ty) !== T.BOLLARD || field(tx, ty) <= V || inTeaKeepOut(trees[i].x / TILE, trees[i].y / TILE)) trees.splice(i, 1); }
  // the planted forest gives way to the hand-set trees and to itself where two trunks would stand closer than TRUNK_GAP
  for (const q of planted) if (!trees.some(o => Math.hypot(o.x - q.x, o.y - q.y) < TRUNK_GAP)) trees.push(q);

  // stone lanterns on the verge, alternating sides, about every 9 tiles of road; torii where the road runs north-south
  const S = sampleRoad(road, 0.25), total = S[S.length - 1].s;
  let side = 1;
  for (let s = 5; s < total - 3; s += 9) {
    const q = S.find(z => z.s >= s)!; side = -side;
    for (const sd of [side, -side]) {
      const n = q.a + sd * Math.PI / 2, x = q.x + Math.cos(n) * (V + 0.45), y = q.y + Math.sin(n) * (V + 0.45);
      if (t(Math.floor(x), Math.floor(y)) === T.BOLLARD) { lanterns.push({ x: x * TILE, y: y * TILE, side: sd }); break; }
    }
  }
  const vertical = (z: RoadSample) => Math.abs(Math.cos(z.a)) < 0.12;
  for (const want of [7, total * 0.47, total - 11]) { const q = S.find(z => z.s >= want && vertical(z)); if (q) torii.push(q); }
  return p;
}

/** the hot spring's pool (tiles): the walkable water that cars and bikes may not enter; the art (hd-sanctuary-country.ts) draws it */
export const ONSEN_POOL = { cx: 308.5, cy: 115, rx: 2.5, ry: 2 };
let waterFeats: { paddies: Feature[]; streams: Feature[] } | undefined;
/** Walkable water in the countryside that a car or bike cannot cross (ponds are solid tiles already): the terraced paddies,
 *  the mountain stream and the onsen pool. Box centre x,y and half-size r in world px. Called from sim.ts boxHits for
 *  vehicles only, so people still wade; server and prediction share it. */
export function carWaterHit(x: number, y: number, r: number): boolean {
  if (x < 282 * TILE - 24 || x >= 328 * TILE || y < 82 * TILE - 24 || y >= 118 * TILE + 24) return false;
  const px = x / TILE, py = y / TILE, rt = r / TILE * 0.6; // a car's corners are rounder than its box
  const W = waterFeats ??= { paddies: FEATURES().filter(f => f.kind === 'paddies'), streams: FEATURES().filter(f => f.kind === 'stream') };
  for (const f of W.paddies) if (px + rt > f.x && px - rt < f.x + f.w && py + rt > f.y && py - rt < f.y + f.h) return true;
  for (const f of W.streams) if (px > f.x - 2 && px < f.x + f.w + 2 && py > f.y - 2 && py < f.y + f.h + 2 && pathDist(f, px, py) < 0.45 + rt) return true;
  const o = ONSEN_POOL;
  return ((px - o.cx) / (o.rx + rt)) ** 2 + ((py - o.cy) / (o.ry + rt)) ** 2 < 1;
}

const trunkCells = new WeakMap<GameMap, Map<number, Tree[]>>();
function trunkGrid(m: GameMap) {
  let g = trunkCells.get(m);
  if (!g) {
    trunkCells.set(m, g = new Map());
    for (const q of countryPlan(m).trees) { const k = Math.floor(q.y / TILE) * m.w + Math.floor(q.x / TILE); const a = g.get(k); if (a) a.push(q); else g.set(k, [q]); }
  }
  return g;
}
/** Does a walker's box (centre x,y, half-size r, world px) touch a tree trunk? Called from sim.ts boxHits for people on
 *  foot and vehicles alike (cars and bikes cross the grass, but a trunk stops them like a wall), so the server and the
 *  client's prediction share it. */
export function trunkHit(m: GameMap, x: number, y: number, r: number): boolean {
  if (x < 258 * TILE - 16 || x >= 334 * TILE + 16 || y < COUNTRY_Y0 * TILE - 16) return false;
  const g = trunkGrid(m), tx = Math.floor(x / TILE), ty = Math.floor(y / TILE);
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const a = g.get((ty + dy) * m.w + tx + dx); if (!a) continue;
    for (const q of a) {
      const nx = Math.max(x - r, Math.min(q.x, x + r)), ny = Math.max(y - r, Math.min(q.y, y + r));
      if ((nx - q.x) ** 2 + (ny - q.y) ** 2 < q.r * q.r) return true;
    }
  }
  return false;
}
